/**
 * Written teasers for the banking module's gated routes
 * (vault/how-to/seamless-page-auth-pattern.md § Part 3).
 *
 * STATIC COPY ONLY. There is no public resolver for bank data, and nothing
 * here imports a page component or runs a query — a signed-out browser must
 * never receive an amount, a balance, a merchant name or a date. The test
 * asserts no teaser contains a `$` or a digit.
 */
export interface BankingPreview {
  title: string;
  blurb: string;
}

/** Keyed by ROUTE TEMPLATE (as in moduleConfig.ts), not by concrete path. */
export const BANKING_PREVIEWS: Record<string, BankingPreview> = {
  "/banking": {
    title: "Your live financial position",
    blurb:
      "Sign in to see the accounts shared with you — current balances, money in and out for the period you choose, and the latest transactions. You land back here as soon as you are in.",
  },
  "/banking/accounts/:id": {
    title: "One of your bank accounts",
    blurb:
      "Sign in to see this account's balance and how fresh it is, a period snapshot, and every transaction on it with filters and an export. Only people granted this account can open it.",
  },
  "/banking/transactions/:id": {
    title: "A transaction on one of your accounts",
    blurb:
      "Sign in to see it — every field the bank gave us, the account it landed on, similar transactions, and any comments or receipts attached to it.",
  },
  "/banking/notifications": {
    title: "Your banking alerts and digests",
    blurb:
      "Sign in to set the alerts that reach you when money moves, choose a daily, weekly or monthly digest by email or text, and review what has been sent to you. Everything is off until you switch it on.",
  },
  "/banking/activity": {
    title: "What has happened to the bank feed",
    blurb:
      "Sign in to read the feed's audit trail — connections, discovered accounts, access changes, webhook deliveries, polls and backfills, newest first. Banking admins only.",
  },
  "/banking/activity/:id": {
    title: "One feed event",
    blurb:
      "Sign in to read the full detail of this event on the bank feed — what happened, who did it and when. Banking admins only.",
  },
  "/banking/month": {
    title: "This month's buckets and money moves",
    blurb:
      "Sign in to see what is safe to spend today after tax and a save target, where the month is heading, and the handful of jobs that move the number. Banking admins only.",
  },
  "/banking/wealth": {
    title: "What you have",
    blurb:
      "Sign in to see your cash, gold and bitcoin together, less what you owe people, in Australian dollars, US dollars and ounces of gold, with the log of what you bought. Banking admins only.",
  },
  "/banking/settings": {
    title: "Connect your bank feed",
    blurb:
      "Sign in to connect Redbark, choose which accounts are enabled and who may see each one, register the webhook, run a poll or a backfill, or disconnect. Banking admins only.",
  },
};

const OBJECT_ID = /^[a-f0-9]{24}$/i;

/** Does a concrete path match a route template segment-for-segment? */
const matches = (template: string, pathname: string): boolean => {
  const t = template.split("/").filter(Boolean);
  const p = pathname.split("/").filter(Boolean);
  if (t.length !== p.length) return false;
  return t.every((seg, i) => (seg.startsWith(":") ? OBJECT_ID.test(p[i]) || p[i].length > 0 : seg === p[i]));
};

/**
 * Longest-template-wins so `/banking/activity/<id>` never falls to `/banking/activity`.
 * Accepts either a route TEMPLATE (what App.tsx hands us) or a concrete URL.
 */
export const bankingPreviewForPath = (pathname: string): BankingPreview | undefined => {
  const clean = pathname.replace(/\/$/, "") || "/";
  if (BANKING_PREVIEWS[clean]) return BANKING_PREVIEWS[clean];
  const hit = Object.keys(BANKING_PREVIEWS)
    .filter((tpl) => matches(tpl, clean))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? BANKING_PREVIEWS[hit] : undefined;
};
